import { config } from '../config.js';
import { countryAt, countryName } from './country-service.js';

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export async function buildRouteAnalysis(points, onProgress = () => {}) {
  if (!Array.isArray(points) || points.length < 2) {
    throw new Error('Potrzebne są co najmniej dwa punkty.');
  }

  const invalidPoints = points
    .map((point, index) => ({ point, index }))
    .filter(({ point }) => !hasValidCoordinates(point));

  if (invalidPoints.length) {
    const names = invalidPoints
      .map(({ point, index }) => `${index + 1}. ${point?.name || 'Bez nazwy'}`)
      .join(', ');
    throw new Error(`Nie można wyznaczyć trasy. Brak poprawnych współrzędnych dla punktów: ${names}.`);
  }

  const enriched = points.map((point, index) => {
    const lat = coordinateNumber(point.lat);
    const lon = coordinateNumber(point.lon);
    const country = countryAt(lon, lat);
    return {
      ...point,
      lat,
      lon,
      id: point.id || crypto.randomUUID(),
      position: index + 1,
      countryCode: country.code,
      countryName: country.name,
      pointType: point.pointType || 'stop',
      isBorder: false,
    };
  });

  const legs = [];
  for (let index = 0; index < enriched.length - 1; index++) {
    onProgress(`Wyznaczam odcinek ${index + 1}/${enriched.length - 1}: ${enriched[index].name} → ${enriched[index + 1].name}`);
    legs.push(await routeLeg(enriched[index], enriched[index + 1], index));
    await sleep(120);
  }

  const expanded = [];
  const sections = [];
  let currentSection = null;

  for (let index = 0; index < legs.length; index++) {
    const from = enriched[index];
    const to = enriched[index + 1];

    if (!expanded.length) expanded.push(from);
    if (!currentSection) currentSection = newSection(from.countryCode, from);

    const transitions = detectTransitions(legs[index]);
    for (const transition of transitions) {
      const border = {
        id: crypto.randomUUID(),
        name: `Granica ${transition.fromCode}/${transition.toCode}`,
        address: '',
        lat: transition.lat,
        lon: transition.lon,
        countryCode: `${transition.fromCode}/${transition.toCode}`,
        countryName: `${countryName(transition.fromCode)} / ${countryName(transition.toCode)}`,
        pointType: 'border',
        isBorder: true,
        sourceType: 'route-border',
        mapsUrl: '',
        borderRoadRef: transition.borderRoadRef || '',
        borderRoadRefs: Array.isArray(transition.borderRoadRefs) ? transition.borderRoadRefs : [],
        borderRoadName: transition.borderRoadName || '',
        borderRoadDistanceKm: Number.isFinite(Number(transition.borderRoadDistanceKm)) ? Number(transition.borderRoadDistanceKm) : null,
        borderRoadSource: transition.borderRoadSource || '',
        position: expanded.length + 1,
      };

      expanded.push(border);
      currentSection.points.push(border);
      currentSection.endBorder = border;
      sections.push(finalizeSection(currentSection, sections.length));
      currentSection = newSection(transition.toCode, border);
    }

    expanded.push(to);
    if (!currentSection) currentSection = newSection(to.countryCode, to);
    else currentSection.points.push(to);

    if (index === legs.length - 1) {
      sections.push(finalizeSection(currentSection, sections.length));
    }
  }

  const validSections = sections.filter(section => section.points.length >= 2);

  // Każdy odcinek państwowy dostaje własny rzeczywisty dystans z OSRM.
  // Dzięki temu raport nie odczytuje napisu skali mapy e-TOLL (np. „100 km”).
  for (let index = 0; index < validSections.length; index++) {
    const section = validSections[index];
    onProgress(`Obliczam dystans odcinka ${section.countryName} (${index + 1}/${validSections.length})…`);
    const metrics = await calculateRouteForPoints(section.points);
    Object.assign(section, metrics);
    await sleep(120);
  }

  const additionalTolls = [
    ...detectPolishConcessionSections(legs, validSections),
    ...detectCroatianTollSections(legs, validSections),
    ...detectEuropeanMotorwayTollSections(validSections),
  ];
  const sectionsWithAdditionalTolls = insertAdditionalTollSections(validSections, additionalTolls)
    .map((section, index) => ({ ...section, order: index + 1 }));

  const allCoords = legs.flatMap((leg, index) =>
    index ? leg.geometry.coordinates.slice(1) : leg.geometry.coordinates
  );

  return {
    originalPoints: enriched,
    expandedPoints: expanded.map((point, index) => ({ ...point, position: index + 1 })),
    legs,
    sections: sectionsWithAdditionalTolls,
    geometry: { type: 'LineString', coordinates: allCoords },
    distanceMeters: legs.reduce((sum, leg) => sum + leg.distance, 0),
    durationSeconds: legs.reduce((sum, leg) => sum + leg.duration, 0),
  };
}

export async function calculateRouteForPoints(points) {
  if (!Array.isArray(points) || points.length < 2) {
    throw new Error('Do obliczenia dystansu potrzebne są co najmniej dwa punkty.');
  }

  const invalid = points.find(point => !hasValidCoordinates(point));
  if (invalid) {
    throw new Error(`Brak poprawnych współrzędnych punktu „${invalid?.name || 'Bez nazwy'}”.`);
  }

  // Publiczny serwer OSRM potrafi zwrócić HTTP 400 dla jednego żądania
  // zawierającego kilka punktów pośrednich, mimo że każda para punktów
  // jest poprawnie routowalna. Dlatego odcinek państwowy liczymy parami.
  // Zapobiega to także niejasnemu błędowi dla całej sekcji i wskazuje
  // dokładnie parę punktów, która ewentualnie nie ma trasy.
  if (points.length === 2) {
    return requestOsrmRoute(points);
  }

  let distanceMeters = 0;
  let durationSeconds = 0;
  const coordinates = [];
  const steps = [];

  for (let index = 0; index < points.length - 1; index++) {
    const from = points[index];
    const to = points[index + 1];
    const leg = await requestOsrmRoute([from, to]);

    distanceMeters += leg.distanceMeters;
    durationSeconds += leg.durationSeconds;

    const legCoordinates = leg.geometry?.coordinates || [];
    if (!coordinates.length) coordinates.push(...legCoordinates);
    else coordinates.push(...legCoordinates.slice(1));
    steps.push(...(leg.steps || []));

    if (index < points.length - 2) await sleep(120);
  }

  return {
    distanceMeters,
    durationSeconds,
    geometry: { type: 'LineString', coordinates },
    steps,
  };
}

async function requestOsrmRoute(points) {
  const coordinates = points
    .map(point => `${coordinateNumber(point.lon)},${coordinateNumber(point.lat)}`)
    .join(';');

  const url = `${config.osrmBaseUrl}/route/v1/driving/${coordinates}?overview=full&geometries=geojson&steps=true&alternatives=false`;
  const response = await fetch(url);

  let data = null;
  let responseText = '';
  try {
    responseText = await response.text();
    data = responseText ? JSON.parse(responseText) : null;
  } catch {
    data = null;
  }

  const first = points[0]?.name || 'Początek';
  const last = points[points.length - 1]?.name || 'Koniec';

  if (!response.ok) {
    const details = data?.message || data?.code || responseText || `HTTP ${response.status}`;
    console.error('[D24 OSRM] Odrzucona para punktów:', {
      from: { name: first, lat: points[0]?.lat, lon: points[0]?.lon },
      to: { name: last, lat: points.at(-1)?.lat, lon: points.at(-1)?.lon },
      status: response.status,
      details,
      url,
    });
    throw new Error(`OSRM: błąd ${response.status} dla ${first} → ${last}: ${details}`);
  }

  const route = data?.routes?.[0];
  if (!route?.geometry?.coordinates?.length) {
    const details = data?.message || data?.code || 'brak geometrii trasy';
    throw new Error(`Brak trasy dla ${first} → ${last}: ${details}.`);
  }

  const steps = (route.legs || []).flatMap(leg => leg.steps || []).map(step => ({
    name: String(step.name || ''),
    ref: String(step.ref || ''),
    distanceMeters: Number(step.distance) || 0,
    durationSeconds: Number(step.duration) || 0,
    geometry: step.geometry || { type: 'LineString', coordinates: [] },
  }));

  return {
    distanceMeters: Number(route.distance) || 0,
    durationSeconds: Number(route.duration) || 0,
    geometry: route.geometry,
    steps,
  };
}

function newSection(code, firstPoint) {
  return {
    id: crypto.randomUUID(),
    countryCode: code,
    countryName: countryName(code),
    points: [firstPoint],
    geometry: { type: 'LineString', coordinates: [] },
    distanceMeters: null,
    durationSeconds: null,
    status: 'ready',
  };
}

function finalizeSection(section, index) {
  return {
    ...section,
    order: index + 1,
    calculatorKey: section.countryCode === 'PL' ? 'etoll-pl' : (section.countryCode === 'CZ' ? 'myto-cz' : (section.countryCode === 'SK' ? 'emyto-sk' : (section.countryCode === 'SI' ? 'darsgo-si' : (section.countryCode === 'AT' ? 'asfinag-at' : (section.countryCode === 'HU' ? 'hugo-hu' : null))))),
  };
}

async function routeLeg(from, to, index) {
  const route = await calculateRouteForPoints([from, to]);
  return {
    index,
    fromId: from.id,
    toId: to.id,
    distance: route.distanceMeters,
    duration: route.durationSeconds,
    geometry: route.geometry,
    steps: route.steps || [],
  };
}

function detectTransitions(leg) {
  const coords = Array.isArray(leg?.geometry?.coordinates) ? leg.geometry.coordinates : [];
  const transitions = [];
  if (coords.length < 2) return transitions;

  let previous = coords[0];
  let previousCountry = countryAt(previous[0], previous[1]).code;

  for (let index = 1; index < coords.length; index++) {
    const current = coords[index];
    const currentCountry = countryAt(current[0], current[1]).code;

    if (previousCountry && currentCountry && previousCountry !== currentCountry) {
      const [lon, lat] = findBorder(previous, current, previousCountry);
      const road = detectBorderRoadReference(
        Array.isArray(leg?.steps) ? leg.steps : [],
        [lon, lat],
        previousCountry,
        currentCountry,
      );
      transitions.push({
        fromCode: previousCountry,
        toCode: currentCountry,
        lon,
        lat,
        coordinateIndex: index,
        ...road,
      });
    }

    if (currentCountry) previousCountry = currentCountry;
    previous = current;
  }

  return transitions;
}

function detectBorderRoadReference(steps, borderCoordinate, fromCode, toCode) {
  const targetCountry = fromCode === 'CZ' || toCode === 'CZ' ? 'CZ' : (toCode || fromCode || '');
  const candidates = [];

  for (const step of steps || []) {
    const roadRefs = extractRoadReferenceCandidates(step?.ref, step?.name);
    if (!roadRefs.length) continue;

    const coordinates = Array.isArray(step?.geometry?.coordinates) ? step.geometry.coordinates : [];
    let nearestAnyKm = Infinity;
    let nearestTargetKm = Infinity;

    for (const coordinate of coordinates) {
      if (!Array.isArray(coordinate) || coordinate.length < 2) continue;
      const lon = Number(coordinate[0]);
      const lat = Number(coordinate[1]);
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
      const distanceKm = haversineKm(borderCoordinate, [lon, lat]);
      nearestAnyKm = Math.min(nearestAnyKm, distanceKm);
      if (!targetCountry || countryAt(lon, lat).code === targetCountry) {
        nearestTargetKm = Math.min(nearestTargetKm, distanceKm);
      }
    }

    const preferredDistanceKm = Number.isFinite(nearestTargetKm) ? nearestTargetKm : nearestAnyKm;
    if (!Number.isFinite(preferredDistanceKm)) continue;

    candidates.push({
      roadRef: roadRefs[0],
      roadRefs,
      roadName: String(step?.name || '').trim(),
      distanceKm: preferredDistanceKm,
      hasTargetCountryPoint: Number.isFinite(nearestTargetKm),
    });
  }

  candidates.sort((a, b) => {
    if (a.hasTargetCountryPoint !== b.hasTargetCountryPoint) return a.hasTargetCountryPoint ? -1 : 1;
    return a.distanceKm - b.distanceKm;
  });

  // Nie zgadujemy numeru drogi z odległego od granicy kroku OSRM.
  const best = candidates.find(candidate => candidate.distanceKm <= 8) || null;
  if (!best) {
    return {
      borderRoadRef: '',
      borderRoadRefs: [],
      borderRoadName: '',
      borderRoadDistanceKm: null,
      borderRoadSource: 'osrm-steps-no-safe-match',
    };
  }

  return {
    borderRoadRef: best.roadRef,
    borderRoadRefs: best.roadRefs,
    borderRoadName: best.roadName,
    borderRoadDistanceKm: Math.round(best.distanceKm * 1000) / 1000,
    borderRoadSource: best.hasTargetCountryPoint ? 'osrm-step-czech-side' : 'osrm-step-nearest-border',
  };
}

function extractRoadReferenceCandidates(refValue, nameValue = '') {
  const source = `${String(refValue || '')} ${String(nameValue || '')}`.toUpperCase();
  const values = [];
  const add = value => {
    const normalized = normalizeBorderRoadRef(value);
    if (normalized && !values.includes(normalized)) values.push(normalized);
  };

  for (const match of source.matchAll(/\bD\s*\d+[A-Z]?\b/g)) add(match[0]);
  for (const match of source.matchAll(/\bI{1,3}\s*\/\s*\d+[A-Z]?\b/g)) add(match[0]);
  for (const match of source.matchAll(/\bE\s*\d+[A-Z]?\b/g)) add(match[0]);

  // OSRM/OSM czasem zwraca samo "48" zamiast "I/48".
  for (const part of String(refValue || '').split(/[;,|]/)) {
    const clean = String(part || '').trim();
    if (/^\d+[A-Z]?$/i.test(clean)) add(clean);
  }

  return values;
}

function normalizeBorderRoadRef(value) {
  return String(value || '')
    .toUpperCase()
    .replace(/[–—−]/g, '-')
    .replace(/\s+/g, '')
    .replace(/^D\/(\d+)/, 'D$1');
}

export async function enrichSectionBorderRoadRefs(section) {
  const points = Array.isArray(section?.points) ? section.points.map(point => ({ ...point })) : [];
  if (points.length < 2) return { ...section, points };

  for (let index = 0; index < points.length; index++) {
    const border = points[index];
    if (!isRouteBorderPoint(border)) continue;
    if (Array.isArray(border.borderRoadRefs) && border.borderRoadRefs.length) continue;
    if (String(border.borderRoadRef || '').trim()) continue;

    const adjacentIndex = index === 0 ? 1 : index === points.length - 1 ? points.length - 2 : null;
    if (adjacentIndex === null) continue;
    const adjacent = points[adjacentIndex];
    if (!hasValidCoordinates(border) || !hasValidCoordinates(adjacent)) continue;

    // Dla początku sekcji jedziemy od granicy do wnętrza CZ, dla końca odwrotnie.
    const routePoints = index === 0 ? [border, adjacent] : [adjacent, border];
    const route = await requestOsrmRoute(routePoints);
    const pair = borderCountryPair(border);
    const road = detectBorderRoadReference(
      route.steps || [],
      [coordinateNumber(border.lon), coordinateNumber(border.lat)],
      pair[0] || '',
      pair[1] || '',
    );
    Object.assign(border, road);
  }

  return { ...section, points };
}

function isRouteBorderPoint(point) {
  return Boolean(
    point?.isBorder
    || point?.pointType === 'border'
    || String(point?.countryCode || '').includes('/')
    || /^granica\b/i.test(String(point?.name || '').trim())
  );
}

function borderCountryPair(point) {
  const codes = String(point?.countryCode || point?.name || '')
    .toUpperCase()
    .match(/\b(?:PL|CZ|SK|AT|DE)\b/g) || [];
  return [...new Set(codes)].slice(0, 2);
}

function findBorder(a, b, fromCode) {
  let left = [...a];
  let right = [...b];

  for (let index = 0; index < 24; index++) {
    const middle = [(left[0] + right[0]) / 2, (left[1] + right[1]) / 2];
    const code = countryAt(middle[0], middle[1]).code;
    if (!code || code === fromCode) left = middle;
    else right = middle;
  }

  return [(left[0] + right[0]) / 2, (left[1] + right[1]) / 2];
}


const A1_AMBERONE_NODES = [
  // Oficjalne punkty kalkulatora AmberOne A1. Współrzędne pochodzą z list
  // Węzeł startowy / Węzeł docelowy zapisanych na stronie operatora.
  { name: 'Rusocin', lon: 18.608516, lat: 54.235883 },
  { name: 'Stanisławie', lon: 18.692258, lat: 54.105700 },
  { name: 'Swarożyn', lon: 18.671055, lat: 54.033170 },
  { name: 'Pelplin', lon: 18.641194, lat: 53.935239 },
  { name: 'Kopytkowo', lon: 18.629942, lat: 53.737726 },
  { name: 'Warlubie', lon: 18.624038, lat: 53.606700 },
  { name: 'Nowe Marzy', lon: 18.614461, lat: 53.468156 },
  { name: 'Grudziądz', lon: 18.693020, lat: 53.427739 },
  { name: 'Lisewo', lon: 18.707729, lat: 53.290304 },
  // Dla trzech ostatnich węzłów strona operatora zapisuje w data-gmap kody
  // Plus Code zamiast współrzędnych, dlatego używamy punktów osi węzłów.
  { name: 'Turzno', lon: 18.757383, lat: 53.093534 },
  { name: 'Lubicz', lon: 18.730640, lat: 53.032710 },
  { name: 'Nowa Wieś', lon: 18.734780, lat: 53.009750 },
];

const A2_NODES = [
  { name: 'Świecko', lon: 14.592, lat: 52.315 },
  { name: 'Rzepin', lon: 14.804, lat: 52.344 },
  { name: 'Torzym', lon: 15.071, lat: 52.309 },
  { name: 'Jordanowo', lon: 15.518, lat: 52.331 },
  { name: 'Trzciel', lon: 15.878, lat: 52.355 },
  { name: 'Nowy Tomyśl', lon: 16.126, lat: 52.329 },
  { name: 'Buk', lon: 16.519, lat: 52.315 },
  { name: 'Poznań Zachód', lon: 16.754, lat: 52.318 },
  { name: 'Poznań Krzesiny', lon: 16.998, lat: 52.314 },
  { name: 'Poznań Wschód', lon: 17.139, lat: 52.329 },
  { name: 'Września', lon: 17.565, lat: 52.326 },
  { name: 'Słupca', lon: 17.884, lat: 52.272 },
  { name: 'Konin', lon: 18.244, lat: 52.237 },
];

const A4_NODES = [
  { name: 'Węzeł Murckowska', lon: 19.074, lat: 50.218 },
  { name: 'Węzeł Mysłowice', lon: 19.139, lat: 50.205 },
  { name: 'Węzeł Brzęczkowice', lon: 19.181, lat: 50.189 },
  { name: 'Węzeł Jeleń', lon: 19.258, lat: 50.166 },
  { name: 'Węzeł Byczyna', lon: 19.334, lat: 50.164 },
  { name: 'Węzeł Balin', lon: 19.397, lat: 50.154 },
  { name: 'Węzeł Chrzanów', lon: 19.459, lat: 50.140 },
  { name: 'Węzeł Rudno', lon: 19.586, lat: 50.104 },
  { name: 'Węzeł Balice', lon: 19.799, lat: 50.087 },
];

const POLISH_CONCESSIONS = [
  {
    calculatorKey: 'a1-amberone-pl',
    roadCode: 'A1',
    displayName: 'Dodatkowa opłata A1 — AmberOne Rusocin–Nowa Wieś',
    operatorName: 'AmberOne / Gdańsk Transport Company',
    nodes: A1_AMBERONE_NODES,
    minPaidDistanceKm: 2.0,
    maxDistanceFromCorridorKm: 2.2,
    isPaidCoordinate: ([lon, lat]) => lon >= 18.54 && lon <= 18.82 && lat >= 52.99 && lat <= 54.26,
  },
  {
    calculatorKey: 'a2-awsa-pl',
    roadCode: 'A2',
    displayName: 'Dodatkowa opłata A2 — Autostrada Wielkopolska',
    operatorName: 'Autostrada Wielkopolska',
    nodes: A2_NODES,
    minPaidDistanceKm: 2.5,
    maxDistanceFromCorridorKm: 1.4,
    isPaidCoordinate: ([lon, lat]) => (
      lon >= 14.54 && lon <= 18.30 && lat >= 52.17 && lat <= 52.45
      && !(lon >= 16.73 && lon <= 17.16)
    ),
  },
  {
    calculatorKey: 'a4-sam-pl',
    roadCode: 'A4',
    displayName: 'Dodatkowa opłata A4 — Katowice–Kraków',
    operatorName: 'Stalexport Autostrada Małopolska',
    nodes: A4_NODES,
    minPaidDistanceKm: 2.0,
    maxDistanceFromCorridorKm: 1.2,
    isPaidCoordinate: ([lon, lat]) => lon >= 19.02 && lon <= 19.84 && lat >= 50.03 && lat <= 50.25,
  },
];

function detectPolishConcessionSections(legs, countrySections) {
  const parent = countrySections.find(section => section.countryCode === 'PL');
  if (!parent) return [];

  return POLISH_CONCESSIONS
    .map(config => detectSingleConcession(config, legs, parent))
    .filter(Boolean);
}

function detectSingleConcession(config, legs, parentSection) {
  const matchedChunks = [];
  let totalDistanceMeters = 0;
  let totalDurationSeconds = 0;

  for (const leg of legs || []) {
    const steps = Array.isArray(leg.steps) ? leg.steps : [];
    let foundRefOnLeg = false;
    let matchedOnLeg = false;

    for (const step of steps) {
      const refText = normalizeRoadRef(`${step.ref || ''} ${step.name || ''}`);
      const refMatches = refText.includes(config.roadCode);
      if (refMatches) foundRefOnLeg = true;
      const coords = step.geometry?.coordinates || [];
      const filtered = coords.filter(coord => concessionCoordinateMatches(config, coord));
      if (!filtered.length || (!refMatches && filtered.length < 2)) continue;

      const ratio = coords.length ? filtered.length / coords.length : 0;
      matchedChunks.push(filtered);
      matchedOnLeg = true;
      totalDistanceMeters += (Number(step.distanceMeters) || 0) * Math.max(0.1, ratio);
      totalDurationSeconds += (Number(step.durationSeconds) || 0) * Math.max(0.1, ratio);
    }

    // Awaryjne wykrywanie dla serwerów OSRM, które nie zwracają pola ref.
    if (!matchedOnLeg) {
      const filtered = (leg.geometry?.coordinates || []).filter(coord => concessionCoordinateMatches(config, coord));
      const approx = polylineLengthMeters(filtered);
      const fallbackMinimum = foundRefOnLeg ? config.minPaidDistanceKm * 1000 : Math.max(5000, config.minPaidDistanceKm * 1000);
      if (approx >= fallbackMinimum) {
        matchedChunks.push(filtered);
        totalDistanceMeters += approx;
        const legDistance = Number(leg.distance) || 0;
        totalDurationSeconds += legDistance > 0 ? (Number(leg.duration) || 0) * (approx / legDistance) : 0;
      }
    }
  }

  const matchedCoordinates = matchedChunks.flat();
  const measuredDistance = polylineChunksLengthMeters(matchedChunks);
  if (measuredDistance < config.minPaidDistanceKm * 1000 && totalDistanceMeters < config.minPaidDistanceKm * 1000) return null;
  if (matchedCoordinates.length < 2) return null;

  const first = matchedCoordinates[0];
  const last = matchedCoordinates.at(-1);
  const entryNode = nearestConcessionNode(config.nodes, first);
  const exitNode = nearestConcessionNode(config.nodes, last);
  if (!entryNode || !exitNode || entryNode.name === exitNode.name) return null;

  const entryPoint = concessionPoint(entryNode, 'entry');
  const exitPoint = concessionPoint(exitNode, 'exit');
  const geometryCoordinates = joinCoordinateChunks(matchedChunks);

  return {
    id: crypto.randomUUID(),
    countryCode: 'PL',
    countryName: 'Polska',
    displayName: config.displayName,
    calculatorKey: config.calculatorKey,
    parentCalculatorKey: 'etoll-pl',
    parentSectionId: parentSection.id,
    isAdditionalToll: true,
    roadCode: config.roadCode,
    operatorName: config.operatorName,
    tollEntryName: entryNode.name,
    tollExitName: exitNode.name,
    points: [entryPoint, exitPoint],
    geometry: { type: 'LineString', coordinates: geometryCoordinates.length >= 2 ? geometryCoordinates : [first, last] },
    distanceMeters: Math.round(Math.max(measuredDistance, totalDistanceMeters)),
    durationSeconds: Math.round(totalDurationSeconds),
    status: 'ready',
  };
}



const AZM_NODES = [
  { name: 'Trakošćan', lon: 15.946, lat: 46.260 },
  { name: 'Đurmanec', lon: 15.840, lat: 46.198 },
  { name: 'Krapina', lon: 15.878, lat: 46.157 },
  { name: 'Sveti Križ Začretje', lon: 15.911, lat: 46.082 },
  { name: 'Zabok', lon: 15.914, lat: 46.027 },
  { name: 'Mokrice', lon: 15.905, lat: 45.957 },
  { name: 'Zaprešić', lon: 15.806, lat: 45.858 },
];

const HAC_A1_NODES = [
  { name: 'Zagreb', lon: 15.891, lat: 45.746 },
  { name: 'Zdenčina', lon: 15.742, lat: 45.700 },
  { name: 'Jastrebarsko', lon: 15.651, lat: 45.665 },
  { name: 'Karlovac', lon: 15.557, lat: 45.523 },
  { name: 'Novigrad', lon: 15.409, lat: 45.487 },
  { name: 'Bosiljevo', lon: 15.283, lat: 45.402 },
  { name: 'Ogulin', lon: 15.228, lat: 45.267 },
  { name: 'Brinje', lon: 15.129, lat: 45.001 },
  { name: 'Žuta Lokva', lon: 15.069, lat: 44.966 },
  { name: 'Otočac', lon: 15.283, lat: 44.870 },
  { name: 'Perušić', lon: 15.383, lat: 44.650 },
  { name: 'Gospić', lon: 15.374, lat: 44.546 },
  { name: 'Gornja Ploča', lon: 15.682, lat: 44.353 },
  { name: 'Sveti Rok', lon: 15.659, lat: 44.309 },
  { name: 'Rovanjska', lon: 15.536, lat: 44.250 },
  { name: 'Posedarje', lon: 15.476, lat: 44.210 },
  { name: 'Zadar centar', lon: 15.360, lat: 44.095 },
  { name: 'Zadar istok', lon: 15.463, lat: 44.071 },
  { name: 'Benkovac', lon: 15.610, lat: 43.997 },
  { name: 'Vodice', lon: 15.779, lat: 43.779 },
  { name: 'Skradin', lon: 15.912, lat: 43.817 },
  { name: 'Šibenik', lon: 15.873, lat: 43.733 },
  { name: 'Podi', lon: 15.917, lat: 43.683 },
  { name: 'Prgomet', lon: 16.204, lat: 43.617 },
  { name: 'Vučevica', lon: 16.380, lat: 43.585 },
  { name: 'Split', lon: 16.572, lat: 43.587 },
  { name: 'Bisko', lon: 16.695, lat: 43.553 },
  { name: 'Blato na Cetini', lon: 16.830, lat: 43.489 },
  { name: 'Šestanovac', lon: 16.912, lat: 43.455 },
  { name: 'Zagvozd', lon: 17.054, lat: 43.355 },
  { name: 'Ravča', lon: 17.383, lat: 43.181 },
  { name: 'Vrgorac', lon: 17.374, lat: 43.193 },
  { name: 'Karamatići', lon: 17.479, lat: 43.050 },
  { name: 'Čarapine', lon: 17.586, lat: 42.988 },
];

const HAC_A6_NODES = [
  { name: 'Bosiljevo', lon: 15.283, lat: 45.402 },
  { name: 'Vrbovsko', lon: 15.079, lat: 45.375 },
  { name: 'Ravna Gora', lon: 14.935, lat: 45.371 },
  { name: 'Delnice', lon: 14.814, lat: 45.399 },
  { name: 'Vrata', lon: 14.714, lat: 45.305 },
  { name: 'Oštrovica', lon: 14.562, lat: 45.330 },
  { name: 'Rijeka (Grobnik)', lon: 14.511, lat: 45.418 },
];

const HAC_A4_NODES = [
  { name: 'Goričan', lon: 16.668, lat: 46.379 },
  { name: 'Čakovec', lon: 16.437, lat: 46.365 },
  { name: 'Varaždin', lon: 16.337, lat: 46.305 },
  { name: 'Varaždinske Toplice', lon: 16.419, lat: 46.205 },
  { name: 'Novi Marof', lon: 16.329, lat: 46.164 },
  { name: 'Breznički Hum', lon: 16.286, lat: 46.106 },
  { name: 'Komin', lon: 16.206, lat: 46.084 },
  { name: 'Sveta Helena', lon: 16.155, lat: 45.974 },
];

const HAC_A3_NODES = [
  { name: 'Bregana', lon: 15.690, lat: 45.842 },
  { name: 'Zagreb zapad', lon: 15.803, lat: 45.816 },
  { name: 'Zagreb istok', lon: 16.170, lat: 45.790 },
  { name: 'Ivanić Grad', lon: 16.395, lat: 45.710 },
  { name: 'Križ', lon: 16.518, lat: 45.653 },
  { name: 'Popovača', lon: 16.625, lat: 45.570 },
  { name: 'Kutina', lon: 16.777, lat: 45.480 },
  { name: 'Novska', lon: 16.970, lat: 45.331 },
  { name: 'Okučani', lon: 17.202, lat: 45.259 },
  { name: 'Nova Gradiška', lon: 17.383, lat: 45.255 },
  { name: 'Slavonski Brod zapad', lon: 17.894, lat: 45.176 },
  { name: 'Slavonski Brod istok', lon: 18.108, lat: 45.154 },
  { name: 'Županja', lon: 18.698, lat: 45.075 },
  { name: 'Lipovac', lon: 19.043, lat: 45.048 },
];

const HAC_A5_NODES = [
  { name: 'Svilaj', lon: 18.489, lat: 45.094 },
  { name: 'Đakovo', lon: 18.410, lat: 45.309 },
  { name: 'Čepin', lon: 18.563, lat: 45.507 },
  { name: 'Osijek', lon: 18.676, lat: 45.552 },
  { name: 'Beli Manastir', lon: 18.603, lat: 45.770 },
];

const HAC_A7_NODES = [
  { name: 'Rupa', lon: 14.283, lat: 45.466 },
  { name: 'Rijeka', lon: 14.446, lat: 45.347 },
  { name: 'Križišće', lon: 14.612, lat: 45.245 },
];

const BINA_NODES = [
  { name: 'Umag', lon: 13.526, lat: 45.430 },
  { name: 'Buje', lon: 13.659, lat: 45.407 },
  { name: 'Nova Vas', lon: 13.666, lat: 45.382 },
  { name: 'Višnjan', lon: 13.720, lat: 45.278 },
  { name: 'Baderna', lon: 13.801, lat: 45.225 },
  { name: 'Kanfanar', lon: 13.839, lat: 45.117 },
  { name: 'Rovinj', lon: 13.713, lat: 45.081 },
  { name: 'Vodnjan', lon: 13.852, lat: 44.967 },
  { name: 'Pula', lon: 13.905, lat: 44.887 },
  { name: 'Žminj', lon: 13.908, lat: 45.143 },
  { name: 'Pazin', lon: 13.938, lat: 45.238 },
  { name: 'Cerovlje', lon: 14.016, lat: 45.278 },
  { name: 'Lupoglav', lon: 14.106, lat: 45.353 },
  { name: 'Tunel Učka', lon: 14.205, lat: 45.315 },
  { name: 'Matulji', lon: 14.325, lat: 45.365 },
];

const CROATIAN_TOLL_CONFIGS = [
  {
    calculatorKey: 'azm-hr',
    roadCodes: ['A2'],
    displayName: 'Autostrada Zagreb–Macelj (A2)',
    operatorName: 'Autocesta Zagreb–Macelj',
    nodes: AZM_NODES,
    nodeChains: [AZM_NODES],
    roadGroup: 'a2',
    minPaidDistanceKm: 3,
    maxDistanceFromCorridorKm: 3.2,
  },
  {
    calculatorKey: 'hac-hr',
    roadCodes: ['A1', 'A6', 'A10', 'D425'],
    displayName: 'HAC — A1/A6/A10',
    operatorName: 'Hrvatske autoceste',
    nodes: [...HAC_A1_NODES, ...HAC_A6_NODES],
    nodeChains: [HAC_A1_NODES, HAC_A6_NODES],
    roadGroup: 'a1',
    minPaidDistanceKm: 3,
    maxDistanceFromCorridorKm: 4.2,
  },
  {
    calculatorKey: 'hac-hr',
    roadCodes: ['A3', 'A5'],
    displayName: 'HAC — A3/A5',
    operatorName: 'Hrvatske autoceste',
    nodes: [...HAC_A3_NODES.slice(2), ...HAC_A5_NODES],
    nodeChains: [HAC_A3_NODES.slice(2), HAC_A5_NODES],
    roadGroup: 'a3',
    minPaidDistanceKm: 8,
    maxDistanceFromCorridorKm: 4.2,
    coordinatePredicate: ([lon]) => lon >= 16.05,
  },
  {
    calculatorKey: 'hac-hr',
    roadCodes: ['A3'],
    displayName: 'HAC — A3 Bregana',
    operatorName: 'Hrvatske autoceste',
    nodes: HAC_A3_NODES.slice(0, 2),
    nodeChains: [HAC_A3_NODES.slice(0, 2)],
    roadGroup: 'bregana',
    minPaidDistanceKm: 2,
    maxDistanceFromCorridorKm: 4.2,
    coordinatePredicate: ([lon, lat]) => lon < 15.94 && lat > 45.75,
  },
  {
    calculatorKey: 'hac-hr',
    roadCodes: ['A4'],
    displayName: 'HAC — A4 Zagreb–Goričan',
    operatorName: 'Hrvatske autoceste',
    nodes: HAC_A4_NODES,
    nodeChains: [HAC_A4_NODES],
    roadGroup: 'a4',
    minPaidDistanceKm: 3,
    maxDistanceFromCorridorKm: 4.2,
  },
  {
    calculatorKey: 'hac-hr',
    roadCodes: ['A7'],
    displayName: 'HAC — A7 Rupa',
    operatorName: 'Hrvatske autoceste',
    nodes: HAC_A7_NODES,
    nodeChains: [HAC_A7_NODES],
    roadGroup: 'rupa',
    minPaidDistanceKm: 2,
    maxDistanceFromCorridorKm: 4.5,
  },
  {
    calculatorKey: 'bina-istra-hr',
    roadCodes: ['A8', 'A9'],
    displayName: 'Bina-Istra — Istarski ipsilon (A8/A9)',
    operatorName: 'Bina-Istra',
    nodes: BINA_NODES,
    nodeChains: [BINA_NODES],
    roadGroup: 'istra',
    minPaidDistanceKm: 3,
    maxDistanceFromCorridorKm: 5,
  },
];

function detectCroatianTollSections(legs, countrySections) {
  const parent = countrySections.find(section => section.countryCode === 'HR');
  if (!parent) return [];
  return CROATIAN_TOLL_CONFIGS
    .map(config => detectCroatianOperator(config, legs, parent))
    .filter(Boolean);
}

function detectCroatianOperator(config, legs, parentSection) {
  const matchedChunks = [];
  const roadCounts = new Map();
  let durationSeconds = 0;

  for (const leg of legs || []) {
    for (const step of leg.steps || []) {
      const refs = extractRoadRefs(`${step.ref || ''} ${step.name || ''}`);
      const matchedRoads = refs.filter(ref => config.roadCodes.includes(ref));
      if (!matchedRoads.length) continue;
      const coords = (step.geometry?.coordinates || []).filter(coordinate => croatianOperatorCoordinateMatches(config, coordinate));
      if (coords.length < 2) continue;
      matchedChunks.push(coords);
      const ratio = Math.min(1, Math.max(0.08, coords.length / Math.max(1, step.geometry?.coordinates?.length || 1)));
      durationSeconds += (Number(step.durationSeconds) || 0) * ratio;
      for (const road of matchedRoads) roadCounts.set(road, (roadCounts.get(road) || 0) + (Number(step.distanceMeters) || polylineLengthMeters(coords)));
    }
  }

  // Awaryjnie: część publicznych serwerów OSRM nie zwraca pola ref.
  if (!matchedChunks.length) {
    for (const leg of legs || []) {
      const filtered = (leg.geometry?.coordinates || []).filter(coordinate => croatianOperatorCoordinateMatches(config, coordinate));
      if (polylineLengthMeters(filtered) >= config.minPaidDistanceKm * 1000) matchedChunks.push(filtered);
    }
  }

  const geometryCoordinates = joinCoordinateChunks(matchedChunks);
  const distanceMeters = polylineChunksLengthMeters(matchedChunks);
  if (geometryCoordinates.length < 2 || distanceMeters < config.minPaidDistanceKm * 1000) return null;

  const first = geometryCoordinates[0];
  const last = geometryCoordinates.at(-1);
  const entryNode = nearestConcessionNode(config.nodes, first);
  const exitNode = nearestConcessionNode(config.nodes, last);
  if (!entryNode || !exitNode || entryNode.name === exitNode.name) return null;

  const dominantRoad = [...roadCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || config.roadCodes[0];
  const roadGroup = config.roadGroup || (config.calculatorKey === 'hac-hr' ? hacRoadGroup(dominantRoad, roadCounts) : '');

  return {
    id: crypto.randomUUID(),
    countryCode: 'HR',
    countryName: 'Chorwacja',
    displayName: config.displayName,
    calculatorKey: config.calculatorKey,
    parentSectionId: parentSection.id,
    isAdditionalToll: true,
    roadCode: dominantRoad,
    roadGroup,
    operatorName: config.operatorName,
    tollEntryName: entryNode.name,
    tollExitName: exitNode.name,
    points: [croatianTollPoint(entryNode, 'entry'), croatianTollPoint(exitNode, 'exit')],
    geometry: { type: 'LineString', coordinates: geometryCoordinates },
    distanceMeters: Math.round(distanceMeters),
    durationSeconds: Math.round(durationSeconds),
    status: 'ready',
  };
}

function croatianOperatorCoordinateMatches(config, coordinate) {
  if (!Array.isArray(coordinate) || coordinate.length < 2) return false;
  if (countryAt(coordinate[0], coordinate[1]).code !== 'HR') return false;
  if (config.coordinatePredicate && !config.coordinatePredicate(coordinate)) return false;
  return config.nodeChains.some(chain => distanceToNodeChainKm(coordinate, chain) <= config.maxDistanceFromCorridorKm);
}

function croatianTollPoint(node, role) {
  return {
    id: crypto.randomUUID(),
    name: node.name,
    address: '',
    lat: node.lat,
    lon: node.lon,
    countryCode: 'HR',
    countryName: 'Chorwacja',
    pointType: role === 'entry' ? 'toll-entry' : 'toll-exit',
    isBorder: false,
    isTollPoint: true,
    sourceType: 'detected-croatian-toll',
  };
}

function extractRoadRefs(value) {
  const matches = String(value || '').toUpperCase().match(/\b(?:A|D)\s*\d+\b/g) || [];
  return [...new Set(matches.map(match => match.replace(/\s+/g, '')))];
}

function hacRoadGroup(dominantRoad, roadCounts) {
  const totals = {
    a1: (roadCounts.get('A1') || 0) + (roadCounts.get('A6') || 0) + (roadCounts.get('A10') || 0) + (roadCounts.get('D425') || 0),
    a3: (roadCounts.get('A3') || 0) + (roadCounts.get('A5') || 0),
    a4: roadCounts.get('A4') || 0,
    a7: roadCounts.get('A7') || 0,
  };
  const selected = Object.entries(totals).sort((a, b) => b[1] - a[1])[0];
  if (selected?.[1] > 0) return selected[0];
  if (['A3', 'A5'].includes(dominantRoad)) return 'a3';
  if (dominantRoad === 'A4') return 'a4';
  if (dominantRoad === 'A7') return 'a7';
  return 'a1';
}



const EUROPEAN_MOTORWAY_TOLLS = [
  {
    countryCode: 'IT',
    countryName: 'Włochy',
    calculatorKey: 'autostrade-it',
    displayName: 'Opłata autostradowa Włochy — Autostrade per l’Italia',
    operatorName: 'Autostrade per l’Italia i współpracujący koncesjonariusze',
    minDistanceKm: 3,
  },
  {
    countryCode: 'FR',
    countryName: 'Francja',
    calculatorKey: 'autoroutes-fr',
    displayName: 'Opłata autostradowa Francja — Autoroutes.fr',
    operatorName: 'ASFA / francuscy operatorzy autostrad',
    minDistanceKm: 3,
  },
];

const ITALY_GATEWAYS = [
  { name: 'Ventimiglia', lon: 7.553, lat: 43.790 },
  { name: 'Courmayeur', lon: 6.968, lat: 45.792 },
  { name: 'Bardonecchia', lon: 6.704, lat: 45.076 },
  { name: 'Chiasso', lon: 9.031, lat: 45.834 },
  { name: 'Brennero', lon: 11.505, lat: 47.003 },
  { name: 'Tarvisio', lon: 13.578, lat: 46.506 },
  { name: 'Trieste', lon: 13.776, lat: 45.649 },
  { name: 'Gorizia', lon: 13.620, lat: 45.941 },
];

const FRANCE_GATEWAYS = [
  { name: 'Calais', lon: 1.858, lat: 50.951 },
  { name: 'Lille', lon: 3.058, lat: 50.633 },
  { name: 'Valenciennes', lon: 3.523, lat: 50.357 },
  { name: 'Thionville', lon: 6.168, lat: 49.358 },
  { name: 'Strasbourg', lon: 7.752, lat: 48.574 },
  { name: 'Mulhouse', lon: 7.335, lat: 47.750 },
  { name: 'Saint-Louis', lon: 7.562, lat: 47.585 },
  { name: 'Chamonix-Mont-Blanc', lon: 6.869, lat: 45.923 },
  { name: 'Modane', lon: 6.660, lat: 45.201 },
  { name: 'Menton', lon: 7.500, lat: 43.775 },
  { name: 'Bayonne', lon: -1.474, lat: 43.493 },
  { name: 'Le Perthus', lon: 2.862, lat: 42.465 },
];

function detectEuropeanMotorwayTollSections(countrySections) {
  return EUROPEAN_MOTORWAY_TOLLS
    .map(config => detectEuropeanMotorwayTollSection(config, countrySections))
    .filter(Boolean);
}

function detectEuropeanMotorwayTollSection(config, countrySections) {
  const parent = countrySections.find(section => section.countryCode === config.countryCode);
  if (!parent) return null;

  const chunks = [];
  let motorwayDistanceMeters = 0;
  let motorwayDurationSeconds = 0;
  const roadCodes = new Set();

  for (const step of parent.steps || []) {
    const roadText = `${step.ref || ''} ${step.name || ''}`;
    if (!looksLikeMotorway(roadText)) continue;
    const coordinates = (step.geometry?.coordinates || [])
      .filter(coordinate => countryAt(coordinate[0], coordinate[1]).code === config.countryCode);
    if (coordinates.length < 2) continue;
    chunks.push(coordinates);
    motorwayDistanceMeters += Number(step.distanceMeters) || polylineLengthMeters(coordinates);
    motorwayDurationSeconds += Number(step.durationSeconds) || 0;
    for (const ref of motorwayRoadRefs(roadText)) roadCodes.add(ref);
  }

  // Awaryjnie dodajemy sprawdzenie opłaty dla dłuższego odcinka kraju, nawet gdy
  // publiczny OSRM nie zwrócił numeru drogi. Oficjalny kalkulator rozstrzygnie,
  // czy wybrana trasa ma rzeczywistą opłatę i zwróci 0 EUR dla odcinka bezpłatnego.
  const parentDistance = Number(parent.distanceMeters) || 0;
  const fallback = chunks.length === 0 && parentDistance >= 30000;
  if (!fallback && motorwayDistanceMeters < config.minDistanceKm * 1000) return null;

  const geometryCoordinates = chunks.length
    ? joinCoordinateChunks(chunks)
    : (parent.geometry?.coordinates || []);
  if (geometryCoordinates.length < 2) return null;

  const selectedPoints = motorwayCalculatorPoints(parent, config.countryCode);
  if (selectedPoints.length < 2) return null;

  return {
    id: crypto.randomUUID(),
    countryCode: config.countryCode,
    countryName: config.countryName,
    displayName: config.displayName,
    calculatorKey: config.calculatorKey,
    parentSectionId: parent.id,
    isAdditionalToll: true,
    roadCode: [...roadCodes].join(', ') || 'AUTO',
    operatorName: config.operatorName,
    tollEntryName: selectedPoints[0].name,
    tollExitName: selectedPoints.at(-1).name,
    points: selectedPoints,
    geometry: { type: 'LineString', coordinates: geometryCoordinates },
    distanceMeters: Math.round(motorwayDistanceMeters || parentDistance),
    durationSeconds: Math.round(motorwayDurationSeconds || Number(parent.durationSeconds) || 0),
    detectionConfidence: fallback ? 'route-country-fallback' : 'osrm-motorway',
    status: 'ready',
  };
}

function looksLikeMotorway(value) {
  const text = String(value || '').toUpperCase();
  return /(?:^|\s)A\s*\d+[A-Z]?(?:\s|$)/.test(text)
    || /\bE\s*\d{1,3}\b/.test(text)
    || /AUTOSTRADA|AUTOROUTE|MOTORWAY|AUTOBAHN/.test(text);
}

function motorwayRoadRefs(value) {
  const text = String(value || '').toUpperCase();
  const matches = text.match(/\b(?:A\s*\d+[A-Z]?|E\s*\d{1,3})\b/g) || [];
  return [...new Set(matches.map(match => match.replace(/\s+/g, '')))];
}

function motorwayCalculatorPoints(parent, countryCode) {
  const source = parent.points || [];
  if (source.length < 2) return [];
  const start = motorwayCalculatorPoint(source[0], countryCode, 'entry');
  const end = motorwayCalculatorPoint(source.at(-1), countryCode, 'exit');
  const middleLimit = countryCode === 'IT' ? 1 : 3;
  const middle = source.slice(1, -1)
    .filter(point => !point.isBorder)
    .slice(0, middleLimit)
    .map(point => motorwayCalculatorPoint(point, countryCode, 'via'));
  return [start, ...middle, end];
}

function motorwayCalculatorPoint(point, countryCode, role) {
  const gateways = countryCode === 'IT' ? ITALY_GATEWAYS : FRANCE_GATEWAYS;
  const coordinate = [Number(point.lon), Number(point.lat)];
  const nearest = gateways
    .map(gateway => ({ ...gateway, distanceKm: haversineKm([gateway.lon, gateway.lat], coordinate) }))
    .sort((a, b) => a.distanceKm - b.distanceKm)[0];
  const originalName = String(point.name || '').trim();
  const useGateway = Boolean(point.isBorder || /^granica\b/i.test(originalName)) && nearest && nearest.distanceKm <= 140;
  const name = useGateway ? nearest.name : (originalName || nearest?.name || `${coordinate[1]}, ${coordinate[0]}`);
  return {
    ...point,
    id: crypto.randomUUID(),
    name,
    searchName: name,
    countryCode,
    countryName: countryCode === 'IT' ? 'Włochy' : 'Francja',
    pointType: role === 'entry' ? 'toll-entry' : role === 'exit' ? 'toll-exit' : 'toll-via',
    isBorder: false,
    isTollPoint: true,
    sourceType: 'detected-european-motorway-toll',
  };
}

function insertAdditionalTollSections(sections, additions) {
  if (!additions.length) return sections;
  const output = [];
  const grouped = new Map();
  for (const addition of additions) {
    const list = grouped.get(addition.parentSectionId) || [];
    list.push(addition);
    grouped.set(addition.parentSectionId, list);
  }
  for (const section of sections) {
    output.push(section);
    output.push(...(grouped.get(section.id) || []));
  }
  return output;
}

function concessionCoordinateMatches(config, coordinate) {
  if (!Array.isArray(coordinate) || coordinate.length < 2 || !config.isPaidCoordinate(coordinate)) return false;
  return distanceToNodeChainKm(coordinate, config.nodes) <= config.maxDistanceFromCorridorKm;
}

function concessionPoint(node, role) {
  return {
    id: crypto.randomUUID(),
    name: node.name,
    address: '',
    lat: node.lat,
    lon: node.lon,
    countryCode: 'PL',
    countryName: 'Polska',
    pointType: role === 'entry' ? 'toll-entry' : 'toll-exit',
    isBorder: false,
    isTollPoint: true,
    sourceType: 'detected-concession',
  };
}

function nearestConcessionNode(nodes, coordinate) {
  return nodes
    .map(node => ({ ...node, distanceKm: haversineKm([node.lon, node.lat], coordinate) }))
    .sort((a, b) => a.distanceKm - b.distanceKm)[0] || null;
}

function normalizeRoadRef(value) {
  return String(value || '').toUpperCase().replace(/\s+/g, '').replace(/AUTOSTRADA/g, 'A');
}

function distanceToNodeChainKm(coordinate, nodes) {
  let best = Infinity;
  for (let index = 0; index < nodes.length - 1; index++) {
    best = Math.min(best, pointToSegmentKm(coordinate, [nodes[index].lon, nodes[index].lat], [nodes[index + 1].lon, nodes[index + 1].lat]));
  }
  return best;
}

function pointToSegmentKm(point, start, end) {
  const meanLat = ((point[1] + start[1] + end[1]) / 3) * Math.PI / 180;
  const scaleX = 111.32 * Math.cos(meanLat);
  const scaleY = 110.57;
  const px = point[0] * scaleX, py = point[1] * scaleY;
  const ax = start[0] * scaleX, ay = start[1] * scaleY;
  const bx = end[0] * scaleX, by = end[1] * scaleY;
  const dx = bx - ax, dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  const t = lengthSquared ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSquared)) : 0;
  const qx = ax + t * dx, qy = ay + t * dy;
  return Math.hypot(px - qx, py - qy);
}

function haversineKm(a, b) {
  const toRad = value => value * Math.PI / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function polylineLengthMeters(coords) {
  let total = 0;
  for (let index = 1; index < coords.length; index++) total += haversineKm(coords[index - 1], coords[index]) * 1000;
  return total;
}

function polylineChunksLengthMeters(chunks) {
  return (chunks || []).reduce((sum, chunk) => sum + polylineLengthMeters(chunk), 0);
}

function joinCoordinateChunks(chunks) {
  const output = [];
  for (const chunk of chunks || []) {
    if (!chunk.length) continue;
    if (!output.length) output.push(...chunk);
    else if (haversineKm(output.at(-1), chunk[0]) < 2) output.push(...chunk.slice(1));
    else output.push(...chunk);
  }
  return output;
}

function coordinateNumber(value) {
  return Number(String(value).replace(',', '.'));
}

function hasValidCoordinates(point) {
  if (!point) return false;
  if (
    point.lat === null || point.lat === undefined || point.lat === '' ||
    point.lon === null || point.lon === undefined || point.lon === ''
  ) return false;

  const lat = coordinateNumber(point.lat);
  const lon = coordinateNumber(point.lon);

  return Number.isFinite(lat) && Number.isFinite(lon) &&
    lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180 &&
    !(lat === 0 && lon === 0);
}
